import { useMemo } from 'react';
import { AssignmentFilters } from '@/components/assignments/assignment-filters';
import { AssignmentCard } from '@/components/assignments/assignment-card';
import { useAssignments } from '@/hooks/use-assignments';
import { useMyProfile } from '@/hooks/use-profile';
import { useAssignmentFilters } from '@/hooks/use-assignment-filters';
import { locationMatchesRegions } from '@/data/swedish-counties';
import { useT, useI18n } from '@/lib/i18n';

export function AssignmentsPage() {
  const t = useT();
  const language = useI18n((s) => s.locale) as 'en' | 'sv';
  const { filters, update } = useAssignmentFilters();
  const { search, regions, status, source } = filters;

  const { data, isLoading } = useAssignments(status);
  const assignments = data?.data ?? [];

  const { data: profileData } = useMyProfile();
  const mySkills = profileData?.data?.skills?.map((s) => s.skillNormalized) ?? [];

  // Collect unique sources for the filter dropdown
  const sources = useMemo(() => {
    const set = new Set(assignments.map((a) => a.source));
    return [...set].sort();
  }, [assignments]);

  const filtered = useMemo(() => {
    let result = assignments;

    if (source) {
      result = result.filter((a) => a.source === source);
    }

    if (search) {
      const lower = search.toLowerCase();
      result = result.filter(
        (a) =>
          a.title.toLowerCase().includes(lower) ||
          (a.location ?? '').toLowerCase().includes(lower) ||
          a.skills.some((s) => s.skill.toLowerCase().includes(lower)) ||
          (a.sourceData?.company ?? '').toLowerCase().includes(lower)
      );
    }

    if (regions.length > 0) {
      result = result.filter((a) => locationMatchesRegions(a.location, regions));
    }

    return result;
  }, [assignments, search, regions, source]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">{t('assignments.title')}</h1>
        <span className="text-xs text-muted-foreground">
          {filtered.length}/{assignments.length}
        </span>
      </div>

      <AssignmentFilters
        search={search}
        onSearchChange={(v) => update({ search: v })}
        regions={regions}
        onRegionsChange={(v) => update({ regions: v })}
        status={status}
        onStatusChange={(v) => update({ status: v })}
        source={source}
        onSourceChange={(v) => update({ source: v })}
        sources={sources}
        language={language}
      />

      {isLoading ? (
        <div className="py-12 text-center text-sm text-muted-foreground">
          {t('assignments.loading')}
        </div>
      ) : filtered.length === 0 ? (
        <div className="py-12 text-center text-sm text-muted-foreground">
          {t('assignments.empty')}
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((assignment) => (
            <AssignmentCard key={assignment.id} assignment={assignment} mySkills={mySkills} />
          ))}
        </div>
      )}
    </div>
  );
}
